'use client';

import Image from 'next/image';
import { LuMail, LuPhone } from 'react-icons/lu';
import { CiLocationOn } from 'react-icons/ci';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useState, useMemo, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { studioClient } from '@/sanity/lib/client';
import { localizedContactInformationQuery } from '@/sanity/queries/contactInformation';
import type { IContactInformation } from '@/types/contactInformation';
import contactUs from '@/assets/imgs/contact-us.svg';
import { Button, Input, LoadingSpinner, Textarea, ToastContainer, useToast } from '@/shared';
import { submitContactForm } from '@/lib/contactForm';

export default function ContactInformation() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactData, setContactData] = useState<IContactInformation | null>(null);
  const [loading, setLoading] = useState(true);
  const t = useTranslations('contactInformation');
  const locale = useLocale();
  const { toast, toasts, removeToast } = useToast();

  // Fetch contact information data
  useEffect(() => {
    const fetchContactInformation = async () => {
      try {
        const data = await studioClient.fetch<IContactInformation>(
          localizedContactInformationQuery(locale)
        );
        setContactData(data);
      } catch {
        console.log('err');
      } finally {
        setLoading(false);
      }
    };

    fetchContactInformation();
  }, [locale]);

  // Create validation schema with useMemo to avoid recreation on every render
  const contactFormSchema = useMemo(
    () =>
      yup.object({
        firstName: yup.string().required(t('validation.firstNameRequired')),
        lastName: yup.string().required(t('validation.lastNameRequired')),
        email: yup
          .string()
          .required(t('validation.emailRequired'))
          .email(t('validation.emailInvalid'))
          .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, t('validation.emailInvalid'))
          .test('real-email', t('validation.emailInvalid'), (value) => {
            if (!value) return false;
            const invalidDomains = ['test.com', 'example.com', 'invalid.com', 'test.test'];
            const domain = value.split('@')[1];
            return !invalidDomains.includes(domain?.toLowerCase());
          }),
        phone: yup
          .string()
          .required(t('validation.phoneRequired'))
          .matches(/^[0-9+\-\s()]+$/, t('validation.phoneInvalid'))
          .min(10, t('validation.phoneMinLength')),
        message: yup.string().max(500, t('validation.messageMaxLength')).optional(),
      }),
    []
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(contactFormSchema),
    mode: 'onChange',
  });

  const onSubmit = async (formData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    message?: string;
  }) => {
    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData);

      if (result.success) {
        toast.success('Message Sent!', 'Thank you for your message. We will get back to you soon.');
        reset();
      } else {
        toast.error('Failed to Send', result.error || 'Please try again later.');
      }
    } catch {
      toast.error('Error', 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <section className="py-16">
        <div className="flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </section>
    );
  }

  if (!contactData) {
    return null;
  }

  const isRTL = locale === 'ar';

  return (
    <section
      className={`max-w-laptop mx-auto px-4 sm:px-6 lg:px-8 ${isRTL ? 'rtl' : 'ltr'}`}
      aria-labelledby="contact-heading"
      dir={isRTL ? 'rtl' : 'ltr'}>
      <div
        className="relative mx-auto mt-10 min-h-[400px] overflow-hidden rounded-2xl p-4 sm:mt-16 sm:min-h-[500px] sm:rounded-3xl sm:p-6 lg:mt-20 lg:min-h-[717px] lg:rounded-4xl lg:p-10"
        style={{
          background: 'rgba(255, 255, 255, 0.04)',
        }}>
        <div className="absolute inset-0 -z-10 bg-[#07BB2B]/10" />
        <Image
          src={contactUs}
          alt="contact-us"
          className={`absolute bottom-0 hidden object-cover lg:block ltr:left-0 rtl:right-1 ${
            isRTL ? 'scale-x-[-1]' : ''
          }`}
          priority={false}
          loading="lazy"
          aria-hidden="true"
        />

        <div
          className={`relative z-10 mx-auto mt-6 flex max-w-[1100px] flex-col items-start justify-center gap-6 sm:mt-8 sm:gap-8 lg:mt-10 lg:flex-row lg:gap-10 ${
            isRTL ? 'lg:flex-row' : ''
          }`}>
          {/* Contact Information Section */}
          <div className="flex w-full flex-col gap-2 text-white sm:gap-3 lg:w-1/2">
            <h2 id="contact-heading" className="text-xl font-bold sm:text-2xl lg:text-3xl">
              {contactData.title}
            </h2>
            <p className="text-xs sm:text-sm lg:text-base">{contactData.description}</p>

            <div className="mt-10 flex items-center gap-10">
              {contactData.branches?.map((branch, index) => (
                <div key={index}>
                  <div>
                    <p className="text-2xl font-medium">{branch.name}</p>
                  </div>
                  <address className="mt-4 flex flex-col gap-3 not-italic sm:mt-5 sm:gap-4">
                    <div className="flex items-center gap-2">
                      <LuPhone className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                      <a
                        dir="ltr"
                        href={`tel:${branch.phone}`}
                        className="text-xs transition-colors hover:text-gray-300 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none sm:text-sm"
                        aria-label={`Call us at ${branch.phone}`}>
                        {branch.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <LuMail className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                      <a
                        href={`mailto:${branch.email}`}
                        className="text-xs transition-colors hover:text-gray-300 focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none sm:text-sm"
                        aria-label={`Email us at ${branch.email}`}>
                        {branch.email}
                      </a>
                    </div>
                    <div className="flex items-start gap-2">
                      <CiLocationOn
                        className="mt-0.5 h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5"
                        aria-hidden="true"
                      />
                      <span className="text-xs text-wrap sm:text-sm">{branch.address}</span>
                    </div>
                  </address>
                </div>
              ))}
            </div>
          </div>

          {/* Form Section */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex w-full flex-col gap-3 sm:gap-4 lg:w-1/2"
            aria-labelledby="contact-heading">
            <div>
              <Input
                label={t('form.firstName')}
                placeholder={t('form.firstNamePlaceholder')}
                {...register('firstName')}
                error={errors.firstName?.message}
              />
            </div>
            <div>
              <Input
                label={t('form.lastName')}
                placeholder={t('form.lastNamePlaceholder')}
                {...register('lastName')}
                error={errors.lastName?.message}
              />
            </div>
            <div>
              <Input
                label={t('form.email')}
                placeholder={t('form.emailPlaceholder')}
                {...register('email')}
                error={errors.email?.message}
              />
            </div>
            <div>
              <Input
                label={t('form.phone')}
                type="number"
                placeholder={t('form.phonePlaceholder')}
                pattern="[0-9+\-\s()]*"
                inputMode="numeric"
                {...register('phone')}
                error={errors.phone?.message}
              />
            </div>
            <div>
              <Textarea
                label={t('form.message')}
                placeholder={t('form.messagePlaceholder')}
                {...register('message')}
                error={errors.message?.message}
                maxLength={500}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className={`${isRTL ? 'self-start' : 'self-end'} px-4 py-2 text-sm transition-colors focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 sm:px-6 sm:py-3 sm:text-base`}
              aria-label="Send contact message">
              {isSubmitting ? t('form.sendingButton') : t('form.sendButton')}
            </Button>
          </form>
        </div>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </section>
  );
}
